import { memo, useEffect, useState } from "react";
import { resolveApiUrl } from "~/utils/url/urlResolver";
import throttle from "lodash/throttle";
import { SessionExpirePopup } from "~/features/auth/session/SessionExpirePopup";
import { doLogout } from "~/features/auth/api/doLogout";
import { userIsLoggedIn } from "~/domains/user/utils/roles";
import { SessionExpiredPopup } from "~/features/auth/session/SessionExpiredPopup";
import { useAppConfig } from "~/app/config/useAppConfig";
import { useDispatch, useSelector } from "react-redux";
import {
  loginRequestedAfterSessionExpiration,
  selectSessionExpirationPhase,
  sessionExpirationFlowDismissed,
} from "~/features/auth/session/sessionExpirationSlice";
import { SessionExpiredLoginPopup } from "~/features/auth/session/SessionExpiredLoginPopup";
import { beginSessionExpiration } from "~/features/auth/session/beginSessionExpiration";

export const useSessionExpirePopup = () => {
  const appConfig = useAppConfig();
  const sessionTimeout = appConfig?.configuration.sessionTimeout ?? 20;
  const autoLogoutMessageTime = appConfig?.configuration.autoLogoutMessageTime ?? 10;
  const sessionExpirationPhase = useSelector(selectSessionExpirationPhase);
  const dispatch = useDispatch();
  const [popupVisible, setPopupVisible] = useState(false);
  const [timeout, setTimeout] = useState(0);
  const [popupConfig, setPopupConfig] = useState(() => {
    return {
      onClose: () => {},
      onLogout: () => {},
      onRefresh: () => {},
    };
  });

  useEffect(() => {
    if (sessionExpirationPhase !== "active" || !userIsLoggedIn()) {
      return;
    }

    // Idle time to show the popup
    const idleToShowPopup = sessionTimeout - autoLogoutMessageTime;
    let intervalId = -1;
    let time = 0;
    setTimeout(sessionTimeout);

    function resetTimer() {
      time = 0;
      clearInterval(intervalId);
      intervalId = setInterval(countIdle, 1000) as unknown as number;
      throttledReset();
    }

    const addListeners = () => events.forEach((event) => window.addEventListener(event, resetTimer));
    const removeListeners = () => events.forEach((event) => window.removeEventListener(event, resetTimer));

    function countIdle() {
      time += 1;
      setTimeout(sessionTimeout - time);

      if (time === idleToShowPopup) {
        removeListeners();
        const refreshAndClose = async () => {
          resetTimer();
          resetBETimer();
          addListeners();
          setPopupVisible(false);
        };

        setPopupConfig((prev) => ({
          ...prev,
          onLogout: async () => {
            await doLogout();
            setPopupVisible(false);
          },
          onRefresh: refreshAndClose,
          onClose: refreshAndClose,
        }));
        setPopupVisible(true);
      }
      if (time === sessionTimeout) {
        clearInterval(intervalId);
        setPopupVisible(false);
        void beginSessionExpiration(dispatch);
      }
    }
    addListeners();
    resetTimer();

    return () => {
      removeListeners();
      clearInterval(intervalId);
    };
  }, [dispatch, sessionTimeout, autoLogoutMessageTime, sessionExpirationPhase]);

  const showLoginPopup = () => {
    dispatch(loginRequestedAfterSessionExpiration());
  };

  let cmp = null;

  if (popupVisible && sessionExpirationPhase === "active") {
    cmp = <SessionExpirePopup {...popupConfig} timeout={timeout} />;
  }

  if (sessionExpirationPhase === "expired") {
    cmp = <SessionExpiredPopup onLogin={showLoginPopup} />;
  }

  if (sessionExpirationPhase === "login") {
    cmp = <SessionExpiredLoginPopup onClose={() => dispatch(sessionExpirationFlowDismissed())} />;
  }

  return cmp;
};

const events = ["load", "mousemove", "mousedown", "touchstart", "click", "keypress"];

const resetBETimer = () => fetch(resolveApiUrl("/refresh-session"));
const throttledReset = throttle(resetBETimer, 1000 * 60, { leading: false });

export const SessionExpirePopupContainer = memo(() => {
  return useSessionExpirePopup();
});
